/**
 * Global shortcuts.
 *
 * The set is small and matches what a Lightroom-trained hand already does:
 * undo, copy/paste settings, hold backslash to see the original, J for
 * clipping, F to fit, 1 for actual pixels, arrows to move through the shoot.
 * Lumos adds two of its own, cmd+backslash for the assistant column and ? for
 * the explanations. Anything typed into a field belongs to that field, never to
 * the app.
 */

import { useEffect, useRef } from 'react';
import { useStore } from '../state/store.ts';

export type ZoomPreset = 'fit' | 'actual';

declare global {
  interface WindowEventMap {
    'lumos:export': CustomEvent<void>;
    'lumos:zoom': CustomEvent<ZoomPreset>;
  }
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Widgets that own the arrow keys: a slider being nudged, a column edge being
 * dragged from the keyboard, the filmstrip moving through its own options.
 * Moving to the next photograph at the same time would be two answers to one
 * keypress.
 */
const ARROW_OWNERS = new Set(['slider', 'separator', 'option', 'spinbutton', 'combobox', 'textbox']);

function ownsArrows(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const role = target.getAttribute('role');
  return role !== null && ARROW_OWNERS.has(role);
}

function step(direction: -1 | 1): void {
  const { photos, selectedId, select } = useStore.getState();
  if (photos.length === 0) return;
  const index = photos.findIndex((photo) => photo.id === selectedId);
  const next = index === -1 ? 0 : index + direction;
  if (next < 0 || next >= photos.length) return;
  select(photos[next].id);
}

export function useKeyboard(): void {
  /** True only while backslash is physically held, so keyup restores the prior state. */
  const holdingBefore = useRef(false);

  useEffect(() => {
    const releaseBefore = () => {
      if (!holdingBefore.current) return;
      holdingBefore.current = false;
      useStore.getState().setShowBefore(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      const store = useStore.getState();
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();

      if (mod) {
        if (event.altKey) return;
        switch (key) {
          case 'z':
            event.preventDefault();
            if (event.shiftKey) store.redo();
            else store.undo();
            return;
          case 'c':
            // A real text selection still belongs to the browser's clipboard.
            if (window.getSelection()?.toString()) return;
            event.preventDefault();
            store.copySettings();
            return;
          case 'v':
            event.preventDefault();
            if (store.clipboard) store.pasteSettings();
            else store.notify('Nothing has been copied yet', 'error');
            return;
          case 's':
            if (!event.shiftKey) return;
            event.preventDefault();
            store.syncToSelection();
            return;
          case 'e':
            event.preventDefault();
            window.dispatchEvent(new CustomEvent('lumos:export'));
            return;
          case '\\':
            event.preventDefault();
            store.toggleChat();
            return;
          default:
            return;
        }
      }

      // Question mark needs shift on most layouts, so it is read before the
      // guard that hands shifted keys back to the browser.
      if (event.key === '?') {
        event.preventDefault();
        store.toggleHelp();
        return;
      }

      if (event.altKey || event.shiftKey) return;

      switch (event.key) {
        case '\\':
          if (event.repeat) return;
          if (!store.showBefore) {
            holdingBefore.current = true;
            store.setShowBefore(true);
          }
          return;
        case 'ArrowLeft':
          if (ownsArrows(event.target)) return;
          event.preventDefault();
          step(-1);
          return;
        case 'ArrowRight':
          if (ownsArrows(event.target)) return;
          event.preventDefault();
          step(1);
          return;
        case '1':
          window.dispatchEvent(new CustomEvent('lumos:zoom', { detail: 'actual' }));
          return;
        default:
          break;
      }

      switch (key) {
        case 'j':
          store.toggleClipping();
          return;
        case 'f':
          window.dispatchEvent(new CustomEvent('lumos:zoom', { detail: 'fit' }));
          return;
        case 'r':
          store.resetRecipe();
          return;
        default:
          break;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === '\\') releaseBefore();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    // Without this, tabbing away mid-hold leaves the viewport stuck on "before".
    window.addEventListener('blur', releaseBefore);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseBefore);
    };
  }, []);
}
